import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, XCircle, Calendar as CalendarIcon, ChevronLeft, ChevronRight, Clock, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { timeSlotsAPI } from '../../utils/api';
import { toast } from 'sonner';
import './CalendarView.css';

const CalendarView = ({ center, onSelectSlot, onBack }) => {
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedDate) {
      loadTimeSlots();
    }
  }, [selectedDate]);

  const loadTimeSlots = async () => {
    setLoading(true);
    try {
      const dateStr = selectedDate.toISOString().split('T')[0];
      const response = await timeSlotsAPI.getAvailable(center.id, dateStr);
      setAvailableSlots(response.data);
    } catch (error) {
      toast.error('Failed to load time slots');
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const isDateAvailable = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(date);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate >= today;
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleDateSelect = (day) => {
    const date = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    if (isDateAvailable(date)) {
      setSelectedDate(date);
      setSelectedTimeSlot(null);
    }
  };

  const handleTimeSlotSelect = (slot) => {
    if (slot.available) {
      setSelectedTimeSlot(slot.time);
    }
  };

  const handleContinue = () => {
    if (selectedDate && selectedTimeSlot) {
      onSelectSlot({
        date: selectedDate.toISOString().split('T')[0],
        timeSlot: selectedTimeSlot
      });
    }
  };

  const changeMonth = (increment) => {
    const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + increment, 1);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (increment < 0 && newMonth < new Date(today.getFullYear(), today.getMonth(), 1)) {
      return;
    }
    
    setCurrentMonth(newMonth);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    const weekDays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(
        <div key={`empty-${i}`} className="aspect-square"></div>
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const isAvailable = isDateAvailable(date);
      const isSelected =
        selectedDate &&
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === month &&
        selectedDate.getFullYear() === year;

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={!isAvailable}
          className={`aspect-square p-2 text-center rounded-lg transition-all duration-150 font-medium text-sm ${
            isSelected
              ? 'bg-orange-600 text-white shadow-md scale-110'
              : isAvailable
              ? 'bg-white text-gray-700 hover:bg-orange-50 hover:text-orange-600 hover:scale-105 cursor-pointer border border-gray-200'
              : 'bg-gray-50 text-gray-300 cursor-not-allowed'
          }`}
        >
          {day}
        </button>
      );
    }

    return (
      <>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {weekDays.map((day, idx) => (
            <div key={idx} className="text-center text-xs font-semibold text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">{days}</div>
      </>
    );
  };

  const isPrevMonthDisabled = () => {
    const today = new Date();
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return prevMonth < new Date(today.getFullYear(), today.getMonth(), 1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-5xl mx-auto bg-white rounded-xl shadow-lg p-6"
      data-testid="calendar-view"
    >
      {/* Compact Header */}
      <div className="flex items-center justify-between mb-6 pb-4 border-b">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-1">
            Select Date & Time
          </h2>
          <div className="flex items-center gap-1 text-sm text-gray-600">
            <MapPin className="h-4 w-4 text-orange-500" />
            <span className="font-medium">{center.name}</span>
          </div>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={onBack}
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Change Center
        </Button>
      </div>

      <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6">
        {/* Calendar Section */}
        <div>
          {/* Month Navigation */}
          <div className="flex items-center justify-between mb-4 bg-gray-800 text-white px-4 py-3 rounded-lg">
            <button
              onClick={() => changeMonth(-1)}
              disabled={isPrevMonthDisabled()}
              className="p-1 hover:bg-white/10 rounded transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Previous month"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <span className="font-semibold text-base">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </span>
            <button
              onClick={() => changeMonth(1)}
              className="p-1 hover:bg-white/10 rounded transition-colors"
              aria-label="Next month"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>

          {/* Calendar Grid */}
          <div className="bg-gray-50 p-4 rounded-lg">
            {renderCalendar()}
          </div>
        </div>

        {/* Time Slots Section */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              <h3 className="font-semibold text-gray-800">Time Slots</h3>
            </div>
            {selectedDate && (
              <span className="text-sm font-medium text-orange-600">
                {formatDate(selectedDate)}
              </span>
            )}
          </div>

          {!selectedDate ? (
            <div className="flex flex-col items-center justify-center h-80 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <CalendarIcon className="h-12 w-12 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">Select a date</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center h-80 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="animate-spin rounded-full h-10 w-10 border-3 border-gray-200 border-t-orange-600 mx-auto mb-3"></div>
                <p className="text-gray-500 text-sm">Loading...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
              {availableSlots.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-80 bg-gray-50 rounded-lg">
                  <XCircle className="h-12 w-12 text-gray-300 mb-3" />
                  <p className="text-gray-500 font-medium">No slots available</p>
                  <p className="text-gray-400 text-sm">Try another date</p>
                </div>
              ) : (
                availableSlots.map((slot) => (
                  <motion.button
                    key={slot.time}
                    whileHover={slot.available ? { scale: 1.02 } : {}}
                    whileTap={slot.available ? { scale: 0.98 } : {}}
                    onClick={() => handleTimeSlotSelect(slot)}
                    disabled={!slot.available}
                    className={
                      `w-full p-3 rounded-lg border transition-all text-left ${
                        selectedTimeSlot === slot.time
                          ? 'border-orange-500 bg-orange-50 shadow-md'
                          : slot.available
                          ? 'border-gray-200 hover:border-orange-300 bg-white hover:shadow-sm'
                          : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      }`
                    }
                    data-testid={`time-slot-${slot.time.replace(/\s/g, '-')}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {slot.available ? (
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-400" />
                        )}
                        <span className={
                          `font-semibold ${
                            slot.available ? 'text-gray-800' : 'text-gray-400'
                          }`
                        }>
                          {slot.time}
                        </span>
                      </div>
                      <span className={
                        `text-xs font-medium px-2 py-1 rounded ${
                          slot.available
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500'
                        }`
                      }>
                        {slot.available ? 'Available' : 'Booked'}
                      </span>
                    </div>
                  </motion.button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Continue Button */}
      <div className="mt-6 pt-4 border-t">
        <Button
          onClick={handleContinue}
          disabled={!selectedDate || !selectedTimeSlot}
          size="lg"
          className="w-full bg-orange-600 hover:bg-orange-700 text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="continue-to-form-btn"
        >
          Continue to Booking Form
        </Button>
      </div>
    </motion.div>
  );
};

export default CalendarView;